import type { MetaFunction } from "@remix-run/node";
import { Link } from "@remix-run/react";
import { SEOLayout } from "~/components/seo/SEOLayout";
import { ContactEmail } from "~/components/contact-email";
import { getSeoMeta } from "~/utils/seo-meta";

const fontClass = "font-['Public Sans'] font-sans";

export const meta: MetaFunction = () =>
  getSeoMeta({
    title: "Privacy Policy - Prizo",
    description:
      "Privacy Policy for Prizo — how we collect, use, and protect your personal information across our Shopify gamification app.",
    url: "/privacy",
  });

export default function Privacy() {
  return (
    <SEOLayout>
      <div className="bg-[#ffffff] min-h-screen w-full">
        <div className="max-w-[1440px] mx-auto px-4 md:px-[120px] py-10 md:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1
                className={`${fontClass} font-bold text-[32px] md:text-[48px] text-[#2c2c2c] mb-4`}
              >
                Privacy Policy
              </h1>
              <p
                className={`${fontClass} text-[16px] md:text-[18px] text-[#6a6a6a] max-w-2xl mx-auto`}
              >
                Your privacy is important to us. This Privacy Policy explains how Prizo collects,
                uses, and protects your personal information.
              </p>
              <p className={`${fontClass} text-[14px] text-[#6a6a6a] mt-4`}>
                Last updated: January 1, 2025
              </p>
            </div>

            <div className="prose prose-lg max-w-none">
              <div className="space-y-8">
                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    1. Information We Collect
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>We collect information you provide directly to us, such as when you:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Create an account or use our services</li>
                      <li>Subscribe to our newsletter</li>
                      <li>Contact us for support</li>
                      <li>Participate in surveys or promotions</li>
                    </ul>
                    <p>
                      This may include your name, email address, phone number, and other contact
                      information.
                    </p>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    2. How We Use Your Information
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>We use the information we collect to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Provide, maintain, and improve our services</li>
                      <li>Process transactions and send related information</li>
                      <li>Send promotional communications (with your consent)</li>
                      <li>Respond to your comments and questions</li>
                      <li>Monitor and analyze usage and trends</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    3. Information Sharing and Disclosure
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      We do not sell, trade, or otherwise transfer your personal information to
                      third parties except as described in this policy:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>With your explicit consent</li>
                      <li>To comply with legal obligations</li>
                      <li>To protect our rights and safety</li>
                      <li>With trusted service providers who assist our operations</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    4. Data Security
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      We implement appropriate technical and organizational measures to protect
                      your personal information against unauthorized access, alteration,
                      disclosure, or destruction. However, no method of transmission over the
                      internet or electronic storage is 100% secure.
                    </p>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    5. Your Rights and Choices
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>You have the right to:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Access and update your personal information</li>
                      <li>Request deletion of your personal information</li>
                      <li>Opt-out of promotional communications</li>
                      <li>Request portability of your data</li>
                    </ul>
                    <p>To exercise these rights, please contact us at <ContactEmail />.</p>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    6. Cookies and Tracking Technologies
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      We use cookies and similar tracking technologies to enhance your experience
                      on our website. You can control cookies through your browser settings, but
                      disabling them may affect the functionality of our services.
                    </p>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    7. Changes to This Privacy Policy
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>
                      We may update this Privacy Policy from time to time. We will notify you of
                      any material changes by posting the new Privacy Policy on this page and
                      updating the "Last updated" date.
                    </p>
                  </div>
                </section>

                <section>
                  <h2
                    className={`${fontClass} font-semibold text-[24px] md:text-[28px] text-[#2c2c2c] mb-4`}
                  >
                    8. Contact Us
                  </h2>
                  <div
                    className={`${fontClass} text-[16px] text-[#4a4a4a] leading-[1.7] space-y-4`}
                  >
                    <p>If you have any questions about this Privacy Policy, please contact us:</p>
                    <ul className="list-none space-y-2">
                      <li>Email: <ContactEmail /></li>
                      <li>
                        Website:{" "}
                        <Link to="/" className="text-[#696cff] hover:underline">
                          prizo.io
                        </Link>
                      </li>
                    </ul>
                  </div>
                </section>
              </div>
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/"
                className={`${fontClass} inline-flex items-center px-8 py-4 bg-[#696cff] text-white rounded-2xl font-medium hover:bg-[#5a5ce6] transition-colors`}
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    </SEOLayout>
  );
}
